<template>
    <div>
        <SectionTitle title="Sprint Counts Over Time">
            <MoreInfo :id="'longitudinal-sprint-more-info'" title="Sprint Counts Over Time">
                <div class="longitudinal-analysis-list">
                    <ul>
                        <li>
                            Sprint Counts Over Time displays each player's number of sprints longitudinally compared to
                            the {{ selectedReferenceGroupText }} data.
                        </li>
                        <slot name="shared-info" />
                    </ul>
                </div>
            </MoreInfo>
        </SectionTitle>
        <div class="d-flex justify-content-center mx-auto" v-if="speedTypeThresholds">
            <RadioButton class="mx-4" :id="'speed-type-radio-group'" :modal="selectedSpeedTypeThreshold"
                :options="speedTypeOptions" :name="'speed-type-options'"
                @update-modal="selectedSpeedTypeThreshold = $event" :task="updateSprintCounts" />
            <DropdownSelector @item-selected="selectDistanceBand" :items="distanceBandOptions"
                :getItemName="distanceBandSelectorDisplayText" :index="distanceBandIdx" />
        </div>
        <MissingDataText v-if="!hasData" message="Insufficient data for player" />
        <div v-else>
            <slot name="legend" />
            <LongitudinalChart :id="selectedSpeedTypeThreshold + '-' + distanceBandIdx + '-counts'"
                :title="titles[selectedDistanceBandId]" :data="sprintDataDisplay" />
        </div>
    </div>
</template>

<script>
import { convertChart, mapKpiCategory } from '@/utils/LongitudinalChartHelper';
import { MeasurementTypes, getMeasurementUnit } from "@/utils/MeasurementSystem";
import {
    generateSpeedTypeOptions,
    generateDistanceBandTitles,
    generateDistanceBandsOptions
} from "@/utils/SpeedBandsHelper";

import SectionTitle from "./SectionTitle.vue";
import MoreInfo from "./MoreInfo.vue";
import RadioButton from "./RadioButton.vue";
import DropdownSelector from "./DropdownSelector.vue";
import LongitudinalChart from "./LongitudinalChart.vue";
import MissingDataText from "./MissingDataText.vue";

export default {
    props: {
        sessions: Array,
        selectedReferenceGroupText: {
            type: String,
            default: 'All Competitions'
        },
        sprintData: Object,
        hsrData: Object,
        comparisonData: Object,
        speedTypeThresholds: Object,
        distanceBands: Array
    },
    components: {
        SectionTitle,
        MoreInfo,
        RadioButton,
        DropdownSelector,
        LongitudinalChart,
        MissingDataText
    },
    data() {
        return {
            sdUnit: getMeasurementUnit(MeasurementTypes.ShortDistance),
            countUnit: getMeasurementUnit(MeasurementTypes.Count),
            hasData: false,
            sprintDataDisplay: null,
            distanceBandIdx: 0,
            selectedDistanceBandId: null,
            selectedSpeedTypeThreshold: null,
            sprintCounts: null,
        };
    },
    computed: {
        titles() {
            return generateDistanceBandTitles(this.distanceBands, this.selectedSpeedTypeThreshold);
        },
        distanceBandOptions() {
            return generateDistanceBandsOptions(this.distanceBands);
        },
        speedTypeOptions() {
            return generateSpeedTypeOptions();
        }
    },
    methods: {
        setInitialConfig() {
            this.selectedSpeedTypeThreshold = 'SPRINT';
            this.selectedDistanceBandId = this.distanceBands[0].id;
        },
        getSprintCountsData() {
            const generateSprintCounts = (playerCountsBySessionId, thresholdKey, speedType) => {
                const playerCounts = playerCountsBySessionId[thresholdKey];
                const comparisonCounts = this.comparisonData[speedType];
                return mapKpiCategory(this.sessions, playerCounts, comparisonCounts ? comparisonCounts[thresholdKey] : null);
            };

            const buildCounts = (data, speedType) => {
                const counts = {};
                this.distanceBands.forEach(band => {
                    counts[band.id] = generateSprintCounts(data, band.id, speedType);
                });
                return counts;
            };

            const counts = {};
            if (this.sprintData) {
                counts.SPRINT = buildCounts(this.sprintData, 'SPRINT');
            }

            if (this.hsrData) {
                counts.HSR = buildCounts(this.hsrData, 'HSR');
            }
            this.sprintCounts = Object.keys(counts).length ? counts : null;
        },
        updateSprintCounts() {
            if (!this.sprintCounts || !this.sprintCounts[this.selectedSpeedTypeThreshold]) {
                this.hasData = false;
                return;
            }
            const selectedData = this.sprintCounts[this.selectedSpeedTypeThreshold][this.selectedDistanceBandId];
            this.hasData = selectedData !== undefined;
            if (this.hasData) {
                this.sprintDataDisplay = convertChart(selectedData, this.sessions, this.countUnit);
            }
        },
        distanceBandSelectorDisplayText(selectedDistanceBandId) {
            return this.distanceBandOptions.find(t => t.value === selectedDistanceBandId.value)?.text || '';
        },
        selectDistanceBand(distanceBandId) {
            this.distanceBandIdx = distanceBandId;
            this.selectedDistanceBandId = this.distanceBands[distanceBandId].id;
            this.updateSprintCounts();
        },
        refreshData() {
            this.getSprintCountsData();
            this.updateSprintCounts();
        },
    },
    watch: {
        data() {
            this.refreshData();
        },
        comparisonData() {
            this.refreshData();
        }
    },
    mounted() {
        this.setInitialConfig();
        this.refreshData();
    }
};
</script>
